import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import regexpPatterns from '@/shared/constants/regexpPatterns';
import Customer from '@/shared/types/customer.type';

type Args = {
  customer: Customer | null;
};

const useAccountDetailsForm = ({ customer }: Args) => {
  const { t } = useTranslation(['customers', 'forms', 'common']);

  const [accountDetailsFormFields, setAccountDetailsFormFields] = useState<
    Partial<Customer>
  >({
    first_name: '',
    last_name: '',
    email_address: '',
    phone_number: '',
  });

  const [accountDetailsFormErrors, setAccountDetailsFormErrors] = useState<
    Partial<Customer>
  >({
    first_name: '',
    last_name: '',
    email_address: '',
    phone_number: '',
  });

  const onUpdateAccountDetails = (values: Record<string, unknown>) => {
    setAccountDetailsFormFields((prev) => ({
      ...prev,
      ...values,
    }));
  };

  const onValidateAccountDetails = () => {
    const errors = {
      first_name: '',
      last_name: '',
      email_address: '',
      phone_number: '',
    };

    if (!accountDetailsFormFields.first_name) {
      errors.first_name = t('forms:first_name_required');
    }

    if (!accountDetailsFormFields.last_name) {
      errors.last_name = t('forms:last_name_required');
    }

    if (!accountDetailsFormFields.email_address) {
      errors.email_address = t('forms:email_address_required');
    } else if (
      !regexpPatterns.emailValidator.test(
        accountDetailsFormFields.email_address,
      )
    ) {
      errors.email_address = t('forms:email_address_invalid');
    }

    if (!accountDetailsFormFields.phone_number) {
      errors.phone_number = t('forms:phone_number_required');
    } else if (
      !regexpPatterns.phoneNumberValidator.test(
        accountDetailsFormFields.phone_number,
      )
    ) {
      errors.phone_number = t('forms:phone_number_invalid');
    }

    setAccountDetailsFormErrors(errors);

    return Object.values(errors).every((error) => !error);
  };

  useEffect(() => {
    if (customer) {
      setAccountDetailsFormFields({
        first_name: customer.first_name,
        last_name: customer.last_name,
        email_address: customer.email_address,
        phone_number: customer.phone_number,
      });
    }
  }, [customer]);

  return {
    accountDetailsFormFields,
    accountDetailsFormErrors,
    onUpdateAccountDetails,
    onValidateAccountDetails,
  };
};

export default useAccountDetailsForm;
